import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiErrorCode, getApiErrorCode, useAlertContext, useDirtyForm } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  DutyScheduleAssignmentChangeRequest,
  DutyScheduleCounselorOptionResponse,
  DutyScheduleDetailResponse,
  dutySchedulesApi,
} from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import {
  DutyScheduleCell,
  cellKey,
  indexSlots,
  matchesCell,
} from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

interface PendingAssignment {
  date: Date;
  columnId: string;
  // null clears the duty rather than removing it.
  counselorId: string | null;
}

// Manual staffing edits, held locally until saved. The saved table and the pending edits stay
// separate, so discarding is just dropping the map and dirtiness is just whether it holds anything.
export function useDutyScheduleAssignments(
  // The id as the route carries it, not dutySchedule.id: this keys the cache write after a save and
  // must match the value the detail query read with. A hand-typed URL can differ in case.
  dutyScheduleId: string | undefined,
  dutySchedule: DutyScheduleDetailResponse | undefined,
  counselors: DutyScheduleCounselorOptionResponse[]
) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const [pending, setPending] = useState<Map<string, PendingAssignment>>(new Map());

  // "Neu berechnen" (recalculate) replaces the whole table and must leave no manual edit standing on
  // it, saved or not — but it only invalidates the query, it does not know this hook's local pending
  // state exists to clear. calculatedAtUtc is what tells a recalculation apart from our own save: a
  // save only ever touches unfilledSlots, so it leaves calculatedAtUtc — and this effect — alone.
  const resetKey = `${dutyScheduleId ?? ""}:${dutySchedule?.calculatedAtUtc?.getTime() ?? ""}`;
  const lastResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (resetKey !== lastResetKeyRef.current) {
      lastResetKeyRef.current = resetKey;
      setPending(new Map());
    }
  }, [resetKey]);

  const savedCells = useMemo(() => indexSlots(dutySchedule?.slots ?? []), [dutySchedule?.slots]);
  const columnsById = useMemo(
    () => new Map((dutySchedule?.columns ?? []).map((column) => [column.id, column])),
    [dutySchedule?.columns]
  );
  const counselorsById = useMemo(() => new Map(counselors.map((c) => [c.id, c])), [counselors]);

  // Resolved locally rather than awaited from the server, so a cell reacts to the click at once. The
  // backend computes the same flag from the same preferences when the save lands.
  const resolvePendingCell = useCallback(
    (assignment: PendingAssignment): DutyScheduleCell => {
      if (assignment.counselorId == null) {
        return { counselorId: null, userCode: null, matchesPreference: true };
      }

      const counselor = counselorsById.get(assignment.counselorId);
      const column = columnsById.get(assignment.columnId);
      if (counselor == null || column == null) {
        return { counselorId: null, userCode: null, matchesPreference: true };
      }

      return {
        counselorId: counselor.id,
        userCode: counselor.userCode,
        matchesPreference: matchesCell(counselor, assignment.date, column.kind, column.courtId),
      };
    },
    [counselorsById, columnsById]
  );

  const resolveCell = useCallback(
    (date: Date, columnId: string): DutyScheduleCell | undefined => {
      const key = cellKey(date, columnId);

      // Checking the saved index first keeps a pending entry impossible for a cell that never existed.
      const saved = savedCells.get(key);
      if (saved == null) {
        return undefined;
      }

      const change = pending.get(key);

      return change == null ? saved : resolvePendingCell(change);
    },
    [savedCells, pending, resolvePendingCell]
  );

  // An assignment only counts as a revert once it resolved: resolvePendingCell reports an unresolvable
  // counselor as { counselorId: null }, which would otherwise look like "cleared" and drop the change.
  const isRevertTo = useCallback(
    (assignment: PendingAssignment, saved: DutyScheduleCell | undefined) => {
      if (assignment.counselorId == null) {
        return saved?.counselorId == null;
      }

      const resolved = resolvePendingCell(assignment).counselorId;

      return resolved != null && resolved === saved?.counselorId;
    },
    [resolvePendingCell]
  );

  const assign = useCallback(
    (date: Date, columnId: string, counselorId: string | null) => {
      const key = cellKey(date, columnId);
      const saved = savedCells.get(key);

      setPending((current) => {
        const next = new Map(current);
        const assignment: PendingAssignment = { date, columnId, counselorId };

        if (isRevertTo(assignment, saved)) {
          next.delete(key);
        } else {
          next.set(key, assignment);
        }

        return next;
      });
    },
    [savedCells, isRevertTo]
  );

  const reset = useCallback(() => setPending(new Map()), []);

  useDirtyForm(pending.size > 0);

  const saveMutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: DutyScheduleAssignmentChangeRequest[] }) =>
      // Handled locally so a concurrent recalculation is reported with a translated message.
      dutySchedulesApi.saveDutyScheduleAssignments(
        { id, saveDutyScheduleAssignmentsRequest: { changes } },
        withLocalErrorHandling
      ),
  });

  // Resolves to whether the save went through, so the release flow can chain on it.
  const save = useCallback(async (): Promise<boolean> => {
    // Nothing to save counts as success, so the release flow is not blocked by it.
    if (dutySchedule == null || dutyScheduleId == null || pending.size === 0) {
      return true;
    }

    const sentKeys = [...pending.keys()];
    const changes: DutyScheduleAssignmentChangeRequest[] = [...pending.values()].map((assignment) => ({
      date: assignment.date,
      columnId: assignment.columnId,
      counselorId: assignment.counselorId,
    }));

    let saved: DutyScheduleDetailResponse;
    try {
      saved = await saveMutation.mutateAsync({ id: dutySchedule.id, changes });
    } catch (error) {
      const errorCode = await getApiErrorCode(error);
      if (errorCode === ApiErrorCode.DutyScheduleSlotNotFound) {
        showError(t("dutySchedule.detail.dataEntry.validation.slotNotFound"));
      } else if (errorCode === ApiErrorCode.DutyScheduleNotEditable) {
        showError(t("dutySchedule.edit.validation.notEditable"));
      } else {
        showError(t("common.requestError"));
      }
      return false;
    }

    // Seeded from the response before the pending edits are dropped. Waiting for a refetch instead
    // would show the pre-edit occupants for the length of the round trip, so a just-saved change
    // would visibly snap back and then reappear.
    queryClient.setQueryData([QueryKey.DutyScheduleDetail, dutyScheduleId], saved);
    // Only drop the keys that were actually sent — a cell edited while the request was in flight
    // must survive the round trip instead of being silently discarded.
    setPending((current) => {
      const next = new Map(current);
      for (const key of sentKeys) {
        next.delete(key);
      }
      return next;
    });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.DutySchedules] });
    // The Verteilung AW/BG tab's duty counts are computed backend-side from the slots this just changed.
    void queryClient.invalidateQueries({ queryKey: [QueryKey.DutyScheduleDistributionSummary] });
    showSuccess(t("dutySchedule.detail.dataEntry.saveSuccess"));

    return true;
  }, [dutySchedule, dutyScheduleId, pending, saveMutation, showError, showSuccess, queryClient, t]);

  return {
    resolveCell,
    assign,
    reset,
    save,
    isDirty: pending.size > 0,
    isSaving: saveMutation.isPending,
  };
}
